import React, { useRef, useEffect, useState } from "react"
import { PastWebinarCard, formatWebinarSchedule } from "./PastWebinarCard"
import { PastWebinarModal } from "./PastWebinarModal"

export { PastWebinarCard, formatWebinarSchedule, PastWebinarModal }

export const PastWebinarsCarousel = ({ webinars = [] }) => {
    const [selectedWebinar, setSelectedWebinar] = useState(null)
    const total = webinars.length
    const carouselRef = useRef(null)

    // Chunk webinars into slides of 3 cards each (desktop: 3 cards per row)
    const chunkSize = 3
    const slides = []
    for (let i = 0; i < webinars.length; i += chunkSize) {
        slides.push(webinars.slice(i, i + chunkSize))
    }

    const totalSlides = slides.length

    // Initialize Bootstrap 5 Carousel
    useEffect(() => {
        if (totalSlides <= 1) return
        if (typeof window !== "undefined" && window.bootstrap?.Carousel && carouselRef.current) {
            const carouselInstance = window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current, {
                interval: 5000,
                wrap: true,
                ride: "carousel",
                pause: "hover",
            })
            return () => {
                try {
                    carouselInstance.dispose()
                } catch (e) {}
            }
        }
    }, [totalSlides])

    // If 0 webinars
    if (total === 0) {
        return (
            <section
                aria-labelledby="past-webinar-title"
                className="row g-0 align-items-stretch overflow-hidden rounded shadow-sm mb-4"
                style={{ backgroundColor: "#f5f7f8" }}
            >
                <div className="col-12 p-4 p-lg-5 text-center">
                    <span
                        className="badge rounded-pill px-3 py-2 mb-3"
                        style={{ backgroundColor: "#e7ebee", color: "#16374e" }}
                    >
                        Past webinar
                    </span>
                    <h4 className="fw-bold mb-1" style={{ color: "#16374e" }}>
                        No Past Webinars Available
                    </h4>
                    <p className="text-muted mb-0">Past webinar sessions will appear here.</p>
                </div>
            </section>
        )
    }

    // If 3 or fewer webinars, show in one row of 3 cards without carousel controls
    if (total <= 3) {
        return (
            <section className="mb-4 mb-lg-5">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <span
                            className="badge rounded-pill px-3 py-2 mb-2"
                            style={{ backgroundColor: "#e7ebee", color: "#16374e" }}
                        >
                            Past webinars
                        </span>
                        <h3 className="fw-bold mb-0" style={{ color: "#16374e" }}>
                            Past Webinar Sessions
                        </h3>
                    </div>
                </div>
                <div className="row g-4 justify-content-start">
                    {webinars.map((webinar, index) => (
                        <div key={webinar.webinarID || index} className="col-12 col-md-6 col-lg-4">
                            <PastWebinarCard
                                webinar={webinar}
                                onClick={(wb) => setSelectedWebinar(wb)}
                            />
                        </div>
                    ))}
                </div>

                <PastWebinarModal
                    webinar={selectedWebinar}
                    onClose={() => setSelectedWebinar(null)}
                />
            </section>
        )
    }

    return (
        <section className="mb-4 mb-lg-5">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <span
                        className="badge rounded-pill px-3 py-2 mb-2"
                        style={{ backgroundColor: "#e7ebee", color: "#16374e" }}
                    >
                        Past webinars
                    </span>
                    <h3 className="fw-bold mb-0" style={{ color: "#16374e" }}>
                        Past Webinar Sessions
                    </h3>
                </div>
            </div>

            {/* Bootstrap Carousel */}
            <div
                ref={carouselRef}
                id="pastWebinarsCarousel"
                className="carousel carousel-dark slide px-0 px-md-3 position-relative"
                data-bs-ride="carousel"
                data-bs-interval="5000"
            >
                {/* Carousel Inner with 3 Cards per Slide */}
                <div className="carousel-inner">
                    {slides.map((slideChunk, slideIdx) => (
                        <div
                            key={slideIdx}
                            className={`carousel-item ${slideIdx === 0 ? "active" : ""}`}
                            data-bs-interval="5000"
                        >
                            <div className="row g-4 justify-content-start">
                                {slideChunk.map((webinar, cardIdx) => (
                                    <div
                                        key={webinar.webinarID || cardIdx}
                                        className="col-12 col-md-6 col-lg-4"
                                    >
                                        <PastWebinarCard
                                            webinar={webinar}
                                            onClick={(wb) => setSelectedWebinar(wb)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bootstrap Carousel Controls */}
                <button
                    className="carousel-control-prev"
                    type="button"
                    data-bs-target="#pastWebinarsCarousel"
                    data-bs-slide="prev"
                    onClick={() => {
                        if (window.bootstrap?.Carousel && carouselRef.current) {
                            window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current).prev()
                        }
                    }}
                    style={{ width: "40px" }}
                >
                    <span className="carousel-control-prev-icon" aria-hidden="true" />
                    <span className="visually-hidden">Previous</span>
                </button>
                <button
                    className="carousel-control-next"
                    type="button"
                    data-bs-target="#pastWebinarsCarousel"
                    data-bs-slide="next"
                    onClick={() => {
                        if (window.bootstrap?.Carousel && carouselRef.current) {
                            window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current).next()
                        }
                    }}
                    style={{ width: "40px" }}
                >
                    <span className="carousel-control-next-icon" aria-hidden="true" />
                    <span className="visually-hidden">Next</span>
                </button>

                {/* Bootstrap Carousel Indicators */}
                <div className="carousel-indicators position-relative mt-4 mb-0">
                    {slides.map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            data-bs-target="#pastWebinarsCarousel"
                            data-bs-slide-to={idx}
                            className={idx === 0 ? "active" : ""}
                            aria-current={idx === 0 ? "true" : undefined}
                            aria-label={`Slide ${idx + 1}`}
                            onClick={() => {
                                if (window.bootstrap?.Carousel && carouselRef.current) {
                                    window.bootstrap.Carousel.getOrCreateInstance(carouselRef.current).to(idx)
                                }
                            }}
                            style={{
                                width: "24px",
                                height: "4px",
                                border: "none",
                            }}
                        />
                    ))}
                </div>
            </div>

            <PastWebinarModal
                webinar={selectedWebinar}
                onClose={() => setSelectedWebinar(null)}
            />
        </section>
    )
}
